import React, { useState, useEffect, useRef } from 'react';
import { FiCheckCircle, FiClock, FiCopy, FiX } from 'react-icons/fi';
import {
    getPaymentDetailsApi,
    getPaymentStatusByIdApi,
    verifyPaymentsMercadoPagoApi,
    cancelPixPaymentApi,
} from '../../Api';
import { UEN_PIX_KEY } from '../../utils';
import { notify } from '../Notify';

// Modal de Pix do perfil. Visual alinhado com PixWaiting (Produtos) e
// Step4Payment (registração) — mesma aparência e mesmas ações: copiar,
// "já paguei", cancelar.
//
// `onClose` é OPCIONAL: quando passado (ex: aberto pelo histórico), mostra o "X"
// e permite fechar clicando fora — sem pagar nem cancelar. No ProfileManager
// (pendência) ele NÃO é passado, então o modal fica travado de propósito.
const ModalPix = ({ paymentId, onSuccess, onClose }) => {
    const [pixData, setPixData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState('pending');
    const [timeLeft, setTimeLeft] = useState('');
    const [isChecking, setIsChecking] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const stoppedRef = useRef(false);

    // 1. Busca inicial dos dados do Pix.
    useEffect(() => {
        let mounted = true;
        const fetchPixInfo = async () => {
            try {
                const response = await getPaymentDetailsApi({ paymentId });
                if (mounted) setPixData(response?.data?.data);
            } catch (err) {
                console.error('Erro ao carregar PIX', err);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        if (paymentId) fetchPixInfo();
        return () => { mounted = false; };
    }, [paymentId]);

    // 2. Contagem regressiva.
    useEffect(() => {
        if (!pixData?.expires_at) return;
        const tick = () => {
            const distance = new Date(pixData.expires_at).getTime() - Date.now();
            if (distance < 0) { setTimeLeft('EXPIRADO'); return false; }
            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((distance % (1000 * 60)) / 1000);
            setTimeLeft(`${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`);
            return true;
        };
        if (!tick()) return;
        const interval = setInterval(() => { if (!tick()) clearInterval(interval); }, 1000);
        return () => clearInterval(interval);
    }, [pixData]);

    // 3. Polling de status.
    useEffect(() => {
        if (status !== 'pending' || loading) return;
        stoppedRef.current = false;
        let timeoutId;
        const poll = async () => {
            if (stoppedRef.current) return;
            try {
                const res = await getPaymentStatusByIdApi({ paymentId });
                if (stoppedRef.current) return;
                if (res.data?.status === 'approved') {
                    setStatus('approved');
                    localStorage.removeItem(UEN_PIX_KEY);
                    onSuccess();
                    return;
                }
            } catch { /* segue tentando */ }
            if (!stoppedRef.current) timeoutId = setTimeout(poll, 10000);
        };
        timeoutId = setTimeout(poll, 10000);
        return () => { stoppedRef.current = true; if (timeoutId) clearTimeout(timeoutId); };
    }, [status, loading, paymentId, onSuccess]);

    const handleManualCheck = async () => {
        if (isChecking || !paymentId) return;
        setIsChecking(true);
        try {
            const res = await verifyPaymentsMercadoPagoApi({ transactionId: paymentId });
            if (res.data?.approved) {
                setStatus('approved');
                localStorage.removeItem(UEN_PIX_KEY);
                onSuccess();
                return;
            }
            notify('Pagamento ainda não confirmado.', 'info');
        } catch {
            notify('Erro na consulta. Tente novamente em instantes.', 'error');
        } finally {
            setIsChecking(false);
        }
    };

    const handleCancelar = async () => {
        if (isCancelling) return;
        setIsCancelling(true);
        stoppedRef.current = true;
        try {
            const res = await cancelPixPaymentApi({ paymentId });
            if (res.data?.approved) {
                notify('Esse Pix já tinha sido pago! Sua carteirinha foi liberada.', 'success');
                setStatus('approved');
                onSuccess();
                return;
            }
            localStorage.removeItem(UEN_PIX_KEY);
            notify('Pagamento cancelado.', 'info');
            onSuccess();
        } catch {
            notify('Não foi possível cancelar agora. Tente de novo.', 'error');
            stoppedRef.current = false;
        } finally {
            setIsCancelling(false);
        }
    };

    const handleCopy = () => {
        if (!pixData?.qr_code) return;
        navigator.clipboard.writeText(pixData.qr_code);
        notify('Código Pix copiado!', 'success');
    };

    const formatExpiration = (isoString) => {
        if (!isoString) return '';
        return new Date(isoString).toLocaleTimeString('pt-BR', {
            timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', hour12: false,
        });
    };

    if (loading) return null;

    return (
        <div
            style={pixStyles.overlay}
            onClick={onClose ? (e) => { if (e.target === e.currentTarget) onClose(); } : undefined}
        >
            <div style={pixStyles.wrap}>
                {onClose && (
                    <button onClick={onClose} style={pixStyles.closeBtn} aria-label="Fechar">
                        <FiX size={18} />
                    </button>
                )}
                {status === 'approved' ? (
                    <div style={pixStyles.aprovado}>
                        <FiCheckCircle size={56} color="#10B981" />
                        <h2 style={pixStyles.aprovadoTitle}>Pagamento Aprovado!</h2>
                        <p style={pixStyles.aprovadoMsg}>Sua solicitação está sendo processada…</p>
                    </div>
                ) : (
                    <>
                        <h2 style={pixStyles.title}>Pague o Pix</h2>
                        <p style={pixStyles.helper}>Escaneie o QR Code ou copie o código abaixo.</p>

                        <div style={pixStyles.timerBox}>
                            <p style={pixStyles.expira}>
                                Expira às <span style={pixStyles.expiraVal}>{formatExpiration(pixData?.expires_at)}</span>
                            </p>
                            <p style={pixStyles.countdown}>
                                <FiClock size={18} /> {timeLeft === 'EXPIRADO' ? 'Código expirado' : timeLeft}
                            </p>
                        </div>

                        {pixData?.qr_code_base64 && (
                            <div style={pixStyles.qrBox}>
                                <img
                                    src={`data:image/png;base64,${pixData.qr_code_base64}`}
                                    alt="QR Code Pix"
                                    style={pixStyles.qrImg}
                                />
                            </div>
                        )}

                        <textarea
                            readOnly
                            value={pixData?.qr_code || ''}
                            rows={3}
                            style={pixStyles.textarea}
                            onClick={(e) => e.target.select()}
                        />

                        <button onClick={handleCopy} style={pixStyles.btnSecondary}>
                            <FiCopy size={16} /> Copiar código Pix
                        </button>

                        <button
                            onClick={handleManualCheck}
                            disabled={isChecking || isCancelling}
                            style={{ ...pixStyles.btnPrimary, opacity: (isChecking || isCancelling) ? 0.6 : 1 }}
                        >
                            {isChecking ? 'Verificando…' : 'Já paguei'}
                        </button>

                        <button
                            onClick={handleCancelar}
                            disabled={isCancelling || isChecking}
                            style={{ ...pixStyles.btnCancel, opacity: (isCancelling || isChecking) ? 0.6 : 1 }}
                        >
                            {isCancelling ? 'Cancelando…' : 'Cancelar pagamento'}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

// Estilo canônico do Pix (compartilhado por cópia entre as 3 telas).
const pixStyles = {
    overlay: {
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(2px)',
        display: 'grid', placeItems: 'center', padding: 20,
    },
    wrap: {
        position: 'relative',
        width: '100%', maxWidth: 420, background: '#FFFFFF',
        border: '1px solid #E2E8F0', borderRadius: 16, padding: '1.5rem',
        textAlign: 'center', fontFamily: "'Inter','Roboto',system-ui,sans-serif",
        maxHeight: '92vh', overflowY: 'auto',
    },
    closeBtn: {
        position: 'absolute', top: 10, right: 10,
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: '#94A3B8', padding: 6, borderRadius: 8, display: 'grid', placeItems: 'center',
    },
    title: { fontSize: '1.25rem', fontWeight: 800, color: '#0F172A', margin: '0 0 4px' },
    helper: { fontSize: '0.88rem', color: '#64748B', margin: '0 0 1rem' },
    timerBox: {
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
        margin: '0 0 1rem', padding: '0.6rem 1rem',
        background: '#F8FAFC', borderRadius: 10, border: '1px solid #E2E8F0',
    },
    expira: { fontSize: '0.78rem', color: '#64748B', margin: 0 },
    expiraVal: { fontWeight: 700, color: '#0F172A' },
    countdown: {
        fontSize: '1.1rem', fontWeight: 700, color: '#E11D48', margin: 0,
        display: 'flex', alignItems: 'center', gap: 6,
    },
    qrBox: {
        background: '#fff', padding: 12, borderRadius: 12, border: '1px solid #E2E8F0',
        display: 'inline-block', boxShadow: '0 4px 12px -2px rgba(15,23,42,0.06)',
    },
    qrImg: { width: 200, height: 200, display: 'block' },
    textarea: {
        width: '100%', marginTop: '1rem', padding: '0.65rem', borderRadius: 10,
        border: '1px solid #E2E8F0', fontSize: '0.8rem', resize: 'none',
        fontFamily: 'inherit', color: '#334155', boxSizing: 'border-box',
    },
    btnPrimary: {
        width: '100%', marginTop: '0.6rem', padding: '0.8rem', borderRadius: 10,
        border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#059669,#10B981)',
        color: '#fff', fontWeight: 700, fontSize: '0.9rem',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: 'inherit',
    },
    btnSecondary: {
        width: '100%', marginTop: '0.6rem', padding: '0.8rem', borderRadius: 10,
        border: '1px solid #CBD5E1', background: '#fff', color: '#475569',
        fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: 'inherit',
    },
    btnCancel: {
        width: '100%', marginTop: '0.5rem', padding: '0.8rem', borderRadius: 10,
        border: '1px solid #FECDD3', background: '#fff', color: '#9F1239',
        fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'inherit',
    },
    aprovado: { padding: '1.5rem 0' },
    aprovadoTitle: { color: '#10B981', margin: '0.75rem 0 0.25rem', fontWeight: 700, fontSize: '1.25rem' },
    aprovadoMsg: { color: '#64748B', margin: 0, fontSize: '0.9rem' },
};

export default ModalPix;
